import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, Image, Table, Loader2, CheckCircle2, Package, FileCheck, Mail, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRateLimit, RATE_LIMIT_PRESETS } from "@/hooks/useRateLimit";

interface TaxYearExportProps {
  open: boolean;
  onClose: () => void;
}

interface ExportSummary {
  totalReceipts: number;
  totalSubtotal: number;
  totalTax: number;
  totalAmount: number;
  totalTaxSavings: number;
  categoryTotals: Record<string, { count: number; subtotal: number; tax: number; total: number; taxSavings: number }>;
  monthlyTotals: Record<string, { count: number; subtotal: number; tax: number; total: number; taxSavings: number }>;
  quarterlyTotals: Record<string, { count: number; subtotal: number; tax: number; total: number; taxSavings: number }>;
}

interface ExportProfile {
  name: string | null;
  business_type: string | null;
  use_case: string | null;
}

const formatCurrency = (amount: number | null | undefined): string => {
  return (amount || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getMonthName = (monthStr: string): string => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const monthNum = parseInt(monthStr.split('-')[1], 10) - 1;
  return months[monthNum] || monthStr;
};

const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

async function generatePDFReport(
  year: string, 
  summary: ExportSummary, 
  profile: ExportProfile
): Promise<Blob> {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF();
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let y = 20;
  
  const primaryColor: [number, number, number] = [34, 139, 34];
  const darkColor: [number, number, number] = [33, 33, 33];
  const grayColor: [number, number, number] = [100, 100, 100];
  const lightGray: [number, number, number] = [245, 245, 245];
  
  const addHeader = (text: string, size: number = 16) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(text, margin, y);
    y += size * 0.5;
  };
  
  const addLine = () => {
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
    y += 5;
  };
  
  const addThinLine = () => {
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(margin, y, pageWidth - margin, y);
    y += 3;
  };
  
  const checkNewPage = (neededSpace: number = 30) => {
    if (y > doc.internal.pageSize.getHeight() - neededSpace) {
      doc.addPage();
      y = 20;
    }
  };
  
  // Title page
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 50, 'F');
  
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('TAX YEAR EXPENSE REPORT', pageWidth / 2, 30, { align: 'center' });
  
  doc.setFontSize(16);
  doc.text(year, pageWidth / 2, 42, { align: 'center' });
  
  y = 70;
  
  // Client Info Box
  doc.setFillColor(...lightGray);
  doc.roundedRect(margin, y, contentWidth, 45, 3, 3, 'F');
  
  y += 12;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkColor);
  doc.text('PREPARED FOR', margin + 10, y);
  
  y += 10;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Client: ${profile.name || 'N/A'}`, margin + 10, y);
  y += 7;
  doc.text(`Account Type: ${profile.use_case === 'business' ? 'Business' : 'Personal'}`, margin + 10, y);
  y += 7;
  if (profile.business_type) {
    doc.text(`Business Type: ${profile.business_type}`, margin + 10, y);
  }
  
  y += 20;
  
  // Executive Summary Box
  doc.setFillColor(...primaryColor);
  doc.roundedRect(margin, y, contentWidth, 70, 3, 3, 'F');
  
  y += 15;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('EXECUTIVE SUMMARY', pageWidth / 2, y, { align: 'center' });
  
  y += 15;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  
  const col1X = margin + 15;
  const col2X = pageWidth / 2 + 10;
  
  doc.text(`Total Receipts: ${summary.totalReceipts}`, col1X, y);
  doc.text(`Subtotal: $${formatCurrency(summary.totalSubtotal)}`, col2X, y);
  y += 8;
  doc.text(`Total Tax Paid: $${formatCurrency(summary.totalTax)}`, col1X, y);
  doc.text(`Grand Total: $${formatCurrency(summary.totalAmount)}`, col2X, y);
  y += 12;
  
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(`Estimated Tax Savings: $${formatCurrency(summary.totalTaxSavings)}`, pageWidth / 2, y, { align: 'center' });
  
  y += 30;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayColor);
  doc.text(`Report Generated: ${new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, y);
  doc.text('Prepared by: TaxGBT', pageWidth - margin, y, { align: 'right' });
  
  // Page 2: Quarterly & Monthly
  doc.addPage();
  y = 20;
  
  addHeader('QUARTERLY ANALYSIS');
  addLine();
  y += 5;
  
  doc.setFillColor(...lightGray);
  doc.rect(margin, y, contentWidth, 8, 'F');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkColor);
  const qColWidths = [30, 25, 35, 30, 35, 35];
  let qX = margin + 3;
  ['Quarter', 'Count', 'Subtotal', 'Tax', 'Total', 'Tax Savings'].forEach((header, i) => {
    doc.text(header, qX, y + 5.5);
    qX += qColWidths[i];
  });
  y += 12;
  
  doc.setFont('helvetica', 'normal');
  Object.entries(summary.quarterlyTotals).forEach(([quarter, data]) => {
    qX = margin + 3;
    const qRow = [
      `${quarter} ${year}`,
      String(data.count),
      `$${formatCurrency(data.subtotal)}`,
      `$${formatCurrency(data.tax)}`,
      `$${formatCurrency(data.total)}`,
      `$${formatCurrency(data.taxSavings)}`
    ];
    qRow.forEach((val, i) => {
      doc.text(val, qX, y);
      qX += qColWidths[i];
    });
    y += 6;
  });
  
  addThinLine();
  doc.setFont('helvetica', 'bold');
  qX = margin + 3;
  const qTotal = [
    'TOTAL',
    String(summary.totalReceipts),
    `$${formatCurrency(summary.totalSubtotal)}`,
    `$${formatCurrency(summary.totalTax)}`,
    `$${formatCurrency(summary.totalAmount)}`,
    `$${formatCurrency(summary.totalTaxSavings)}`
  ];
  qTotal.forEach((val, i) => {
    doc.text(val, qX, y);
    qX += qColWidths[i];
  });
  
  y += 20;
  
  addHeader('MONTHLY BREAKDOWN');
  addLine();
  y += 5;
  
  doc.setFillColor(...lightGray);
  doc.rect(margin, y, contentWidth, 8, 'F');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  const mColWidths = [40, 20, 35, 30, 35, 30];
  let mX = margin + 3;
  ['Month', 'Count', 'Subtotal', 'Tax', 'Total', 'Tax Savings'].forEach((header, i) => {
    doc.text(header, mX, y + 5.5);
    mX += mColWidths[i];
  });
  y += 12;
  
  doc.setFont('helvetica', 'normal');
  Object.entries(summary.monthlyTotals)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .forEach(([month, data]) => {
      checkNewPage(15);
      mX = margin + 3;
      const mRow = [
        getMonthName(month),
        String(data.count),
        `$${formatCurrency(data.subtotal)}`,
        `$${formatCurrency(data.tax)}`,
        `$${formatCurrency(data.total)}`,
        `$${formatCurrency(data.taxSavings)}`
      ];
      mRow.forEach((val, i) => {
        doc.text(val, mX, y);
        mX += mColWidths[i];
      });
      y += 6;
    });
  
  // Page 3: Categories
  doc.addPage();
  y = 20;
  
  addHeader('EXPENSE CATEGORY BREAKDOWN');
  addLine();
  y += 5;
  
  doc.setFillColor(...lightGray);
  doc.rect(margin, y, contentWidth, 8, 'F');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  const cColWidths = [55, 20, 35, 35, 25];
  let cX = margin + 3;
  ['Category', 'Count', 'Total', 'Tax Savings', '% of Total'].forEach((header, i) => {
    doc.text(header, cX, y + 5.5);
    cX += cColWidths[i];
  });
  y += 12;
  
  doc.setFont('helvetica', 'normal');
  Object.entries(summary.categoryTotals)
    .sort((a, b) => b[1].total - a[1].total)
    .forEach(([category, data]) => {
      checkNewPage(15);
      cX = margin + 3;
      const pct = summary.totalAmount > 0 ? ((data.total / summary.totalAmount) * 100).toFixed(1) : '0.0';
      const cRow = [
        category.substring(0, 28),
        String(data.count),
        `$${formatCurrency(data.total)}`,
        `$${formatCurrency(data.taxSavings)}`,
        `${pct}%`
      ];
      cRow.forEach((val, i) => {
        doc.text(val, cX, y);
        cX += cColWidths[i];
      });
      y += 6;
    });
  
  y += 15;
  checkNewPage(60);
  
  addHeader('NOTES FOR YOUR ACCOUNTANT', 12);
  addLine();
  y += 5;
  
  const notes = [
    '1. This report contains a complete summary of all expense receipts for the tax year.',
    '2. Original receipt images are included in the accompanying ZIP file, organized by category.',
    '3. The CSV file provides detailed transaction data compatible with accounting software.',
    '4. Tax savings estimates are calculated based on applicable business deduction rates.',
    '5. Receipts marked as "needs_review" may require additional verification.',
    '6. All amounts are in Canadian Dollars (CAD) unless otherwise specified.'
  ];
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkColor);
  notes.forEach(note => {
    checkNewPage(10);
    doc.text(note, margin + 5, y);
    y += 5;
  });
  
  y += 15;
  
  doc.setFillColor(...lightGray);
  doc.rect(margin, y, contentWidth, 25, 'F');
  y += 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayColor);
  doc.text('This report was generated by TaxGBT - Your AI Tax Assistant', pageWidth / 2, y, { align: 'center' });
  y += 7;
  doc.setFontSize(8);
  doc.text(`Report Date: ${new Date().toISOString()}`, pageWidth / 2, y, { align: 'center' });
  
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...grayColor);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
  }
  
  return doc.output('blob');
}

export default function TaxYearExport({ open, onClose }: TaxYearExportProps) {
  const { toast } = useToast();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState("");
  const [activeTab, setActiveTab] = useState("download");
  
  const [accountantEmail, setAccountantEmail] = useState("");
  const [accountantName, setAccountantName] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  const { checkLimit, isLimited } = useRateLimit(RATE_LIMIT_PRESETS.export);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const fetchExportData = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not authenticated");

    const { data, error } = await supabase.functions.invoke('export-tax-year', {
      body: { year: selectedYear }
    });

    if (error) throw error;
    if (!data.success) throw new Error(data.error || "Export failed");

    return data;
  };

  const handleEmailExport = async () => {
    if (!accountantEmail) {
      toast({
        title: "Email Required",
        description: "Please enter your accountant's email address",
        variant: "destructive",
      });
      return;
    }

    if (!checkLimit()) return;

    setIsSendingEmail(true);
    setExportProgress("Preparing export data...");

    try {
      const data = await fetchExportData();
      
      setExportProgress("Generating PDF report...");
      const pdfBlob = await generatePDFReport(selectedYear, data.summary, data.profile);
      const pdfBase64 = await blobToBase64(pdfBlob);
      
      setExportProgress("Sending email to accountant...");
      
      const { data: emailResult, error: emailError } = await supabase.functions.invoke('email-tax-export', {
        body: {
          accountantEmail,
          accountantName: accountantName || undefined,
          year: selectedYear,
          pdfBase64,
          csvContent: data.files.transactions_csv,
          summary: {
            totalReceipts: data.summary.totalReceipts,
            totalAmount: data.summary.totalAmount,
            totalTax: data.summary.totalTax,
            totalTaxSavings: data.summary.totalTaxSavings,
          },
          clientName: data.profile.name || 'Client',
          personalMessage: personalMessage || undefined,
        }
      });

      if (emailError) throw emailError;
      if (!emailResult.success) throw new Error(emailResult.error || "Failed to send email");

      toast({
        title: "✅ Email Sent!",
        description: `Tax export has been sent to ${accountantEmail}`,
      });

      setAccountantEmail("");
      setAccountantName("");
      setPersonalMessage("");
      onClose();
    } catch (error: any) {
      console.error("Email export error:", error);
      toast({
        title: "Failed to Send Email",
        description: error.message || "Could not send the export email",
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
      setExportProgress("");
    }
  };

  const handleExport = async () => {
    if (!checkLimit()) return;
    
    setIsExporting(true);
    setExportProgress("Preparing your tax export...");

    try {
      setExportProgress("Fetching receipts and data...");
      const data = await fetchExportData();

      setExportProgress("Generating PDF report...");
      const pdfBlob = await generatePDFReport(selectedYear, data.summary, data.profile);

      setExportProgress("Creating export package...");

      const { default: JSZip } = await import("jszip");
      const { saveAs } = await import("file-saver");
      const zip = new JSZip();

      zip.file(`Tax_Year_${selectedYear}_Report.pdf`, pdfBlob);
      zip.file(`Tax_Year_${selectedYear}_Summary.txt`, data.files.summary_report);
      zip.file(`Tax_Year_${selectedYear}_Transactions.csv`, data.files.transactions_csv);

      setExportProgress(`Downloading ${data.files.receipt_files.length} receipt images...`);
      const receiptsFolder = zip.folder(`Receipts_${selectedYear}`);

      if (receiptsFolder && data.files.receipt_files.length > 0) {
        const receiptsByCategory: Record<string, any[]> = {};
        data.files.receipt_files.forEach((receipt: any) => {
          if (!receiptsByCategory[receipt.category]) {
            receiptsByCategory[receipt.category] = [];
          }
          receiptsByCategory[receipt.category].push(receipt);
        });

        let downloadedCount = 0;
        for (const [category, receipts] of Object.entries(receiptsByCategory)) {
          const categoryFolder = receiptsFolder.folder(category);
          
          if (categoryFolder) {
            for (const receipt of receipts) {
              try {
                const { data: signedUrl, error: signError } = await supabase.storage
                  .from('receipts')
                  .createSignedUrl(receipt.file_path, 3600);

                if (signError) throw signError;
                
                if (signedUrl?.signedUrl) {
                  const fileExt = receipt.file_path.split('.').pop();
                  const fileName = `${receipt.date}_${receipt.merchant || 'Receipt'}_$${receipt.total?.toFixed(2)}.${fileExt}`;
                  
                  const response = await fetch(signedUrl.signedUrl);
                  const blob = await response.blob();
                  categoryFolder.file(fileName, blob);
                  
                  downloadedCount++;
                  setExportProgress(`Downloaded ${downloadedCount}/${data.files.receipt_files.length} receipts...`);
                }
              } catch (err) {
                console.error(`Failed to download receipt ${receipt.id}:`, err);
              }
            }
          }
        }
      }

      const readme = `TAX YEAR ${selectedYear} EXPORT PACKAGE
════════════════════════════════════════

This package contains all your tax-related documents for the year ${selectedYear}.

CONTENTS
────────
1. Tax_Year_${selectedYear}_Report.pdf - Professional PDF report for your accountant
2. Tax_Year_${selectedYear}_Summary.txt - Detailed text summary report
3. Tax_Year_${selectedYear}_Transactions.csv - Complete transaction data
4. Receipts_${selectedYear}/ - All receipt images organized by category

SUMMARY
───────
Total Receipts: ${data.summary.totalReceipts}
Total Expenses: $${formatCurrency(data.summary.totalAmount)} CAD
Est. Tax Savings: $${formatCurrency(data.summary.totalTaxSavings)}

────────────────────────────────────────
Generated by TaxGBT - Your AI Tax Assistant
${new Date().toISOString()}`;

      zip.file('README.txt', readme);

      setExportProgress("Creating ZIP file...");

      const blob = await zip.generateAsync({ 
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 }
      });

      saveAs(blob, `Tax_Year_${selectedYear}_Complete_Export.zip`);

      toast({
        title: "✅ Export Complete!",
        description: `Tax year ${selectedYear} data has been downloaded.`,
      });

      onClose();
    } catch (error: any) {
      console.error("Export error:", error);
      toast({
        title: "Export Failed",
        description: error.message || "Failed to export tax data",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
      setExportProgress("");
    }
  };

  const isProcessing = isExporting || isSendingEmail;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Export Tax Year Data
          </DialogTitle>
          <DialogDescription>
            Download or email your complete tax package to your accountant
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!isProcessing ? (
            <>
              <div className="space-y-2">
                <Label>Select Tax Year</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(year => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="download" className="flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Download
                  </TabsTrigger>
                  <TabsTrigger value="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="download" className="space-y-4 mt-4">
                  <Card className="p-4 space-y-3 bg-secondary/50">
                    <h4 className="font-semibold text-sm">Package Includes:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <FileCheck className="w-4 h-4 text-primary" />
                        <span>Professional PDF report</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        <span>Detailed text summary</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Table className="w-4 h-4 text-primary" />
                        <span>CSV with all transactions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Image className="w-4 h-4 text-primary" />
                        <span>Receipt images by category</span>
                      </div>
                    </div>
                  </Card>

                  <Button className="w-full" onClick={handleExport} disabled={isLimited()}>
                    <Download className="w-4 h-4 mr-2" />
                    Download ZIP Package
                  </Button>
                </TabsContent>

                <TabsContent value="email" className="space-y-4 mt-4">
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="accountantEmail">Accountant's Email *</Label>
                      <Input
                        id="accountantEmail"
                        type="email"
                        placeholder="accountant@example.com"
                        value={accountantEmail}
                        onChange={(e) => setAccountantEmail(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="accountantName">Accountant's Name (optional)</Label>
                      <Input
                        id="accountantName"
                        type="text"
                        placeholder="John Smith, CPA"
                        value={accountantName}
                        onChange={(e) => setAccountantName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="personalMessage">Personal Message (optional)</Label>
                      <Textarea
                        id="personalMessage"
                        placeholder="Add a note for your accountant..."
                        value={personalMessage}
                        onChange={(e) => setPersonalMessage(e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>

                  <Card className="p-3 bg-muted/50">
                    <p className="text-xs text-muted-foreground">
                      <CheckCircle2 className="w-3 h-3 inline mr-1" />
                      Email includes PDF report + CSV transactions. Receipt images are available in the ZIP download.
                    </p>
                  </Card>

                  <Button 
                    className="w-full" 
                    onClick={handleEmailExport} 
                    disabled={isLimited() || !accountantEmail}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send to Accountant
                  </Button>
                </TabsContent>
              </Tabs>
            </>
          ) : (
            <div className="py-8 text-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
              <div>
                <p className="font-medium">{exportProgress}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  This may take a minute...
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
